import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { BuildingContactView, BuildingView, ContactView, SearchBuildingsForContactParameters } from '@api-proxies';
import { BuildingContactService } from '@http/clients/building-contact.service';
import { BuildingService } from '@http/clients/building.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { GridService } from '@services/grid.service';
import { IKebabAction } from '@shared/grids/custom-cell-actions/custom-cell-actions.component';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditBuildingContactComponent } from '@shared/modals/modal-edit-building-contact/modal-edit-building-contact.component';
import { TypeAheadBuildingsComponent } from '@shared/type-ahead-buildings/type-ahead-buildings.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { CONTACT_BUILDINGS_GRID_COLUMNS } from './contacts-single-buildings.config';

@Component({
    selector: 'app-contacts-single-buildings',
    templateUrl: './contacts-single-buildings.component.html',
    styleUrls: ['./contacts-single-buildings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadBuildingsComponent]
})
export class ContactsSingleBuildingsComponent {
    contact = input<ContactView>();

    tabCounterChange = output<void>();

    buildingsDatasource?: IServerSideDatasource;
    gridColumns: ColDef[] = [...CONTACT_BUILDINGS_GRID_COLUMNS];

    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);

    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Contact
    };

    private readonly buildingService = inject(BuildingService);
    private readonly buildingContactService = inject(BuildingContactService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly modalService = inject(ModalService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();

        effect(() => {
            const currentContact = this.contact();
            const contactId = currentContact?.id;
            const organizationId = currentContact?.organizationId;

            if (!contactId) return;

            this.typeAheadSearchByEntityParameters = {
                entityKeyName: EntityTypes.Contact,
                entity: {
                    contactId,
                    organizationId
                }
            };

            if (!this.buildingsDatasource) {
                this.createDatasource();
                return;
            }

            this.refreshGridView();
        });
    }

    getRowIdFn = (params: any) => params.data?.buildingId?.toString();

    contactId(): number | undefined {
        return this.contact()?.id;
    }

    async refreshGridView(): Promise<void> {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    async onSelectedBuilding(building: BuildingView): Promise<void> {
        const contactId = this.contactId();
        if (!building || !contactId) return;
        try {
            const buildingSaved = await this.buildingContactService.save({
                contactId,
                buildingId: building.id
            } as BuildingContactView);

            if (buildingSaved) {
                this.tabCounterChange.emit();
                const gridApi = this.gridService.getGridApi();
                let updated = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [buildingSaved]
                    });
                    updated = transactionResult?.status === 'Applied';
                }

                if (!updated) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
            }
        } catch (error) {
            console.error(error);
            this.snackbarNotificationService.error('An error occurred while trying to save the building');
        }
    }

    async editItem(buildingContact: BuildingContactView): Promise<void> {
        const buildingContactUpdated = await this.modalService.open(ModalEditBuildingContactComponent, {
            contact: buildingContact
        });

        if (buildingContactUpdated) {
            const gridApi = this.gridService.getGridApi();
            if (!gridApi) return;

            const node = gridApi.getRowNode(buildingContact.buildingId.toString());

            if (node?.data) {
                Object.assign(node.data, buildingContactUpdated);
                gridApi.redrawRows({ rowNodes: [node] });
            }
        }
    }

    async removeItem(buildingContact: BuildingContactView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove building',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this building from the building list?',
            title: 'Remove building'
        });

        if (!responseOk) return;

        try {
            const { buildingId } = buildingContact;
            const contactId = this.contactId();
            if (!buildingId || !contactId) return;

            await this.buildingContactService.remove({
                contactId,
                buildingId
            } as BuildingContactView);

            this.tabCounterChange.emit();
            const gridApi = this.gridService.getGridApi();
            let removed = false;

            if (gridApi?.applyServerSideTransaction) {
                const node = gridApi.getRowNode(buildingId.toString());
                if (node?.data) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        remove: [node.data]
                    });
                    removed = transactionResult?.status === 'Applied';
                }
            }

            if (removed) {
                this.noResults.set((gridApi?.getDisplayedRowCount() ?? 0) === 0);
            } else {
                this.refreshGridView();
            }
        } catch (error) {
            console.error(error);
            this.snackbarNotificationService.error('An error occurred while trying to delete the building');
        }
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit',
                action: (buildingContact: BuildingContactView) => this.editItem(buildingContact)
            },
            {
                label: 'Remove',
                action: (buildingContact: BuildingContactView) => this.removeItem(buildingContact)
            }
        ];

        this.gridColumns = CONTACT_BUILDINGS_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchBuildingsForContactParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const sort = sortModel?.length ? sortModel[0] : undefined;

        const contactId = this.contact()?.id;
        if (!contactId) {
            throw new Error('Contact is required to search buildings for contact.');
        }

        const searchParams = {
            pageNumber,
            pageSize,
            ...(sort?.sort
                ? {
                      sortBy: sort.colId,
                      sortDirection: sort.sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            contactId,
            organizationId: this.contact()?.organizationId
        } as SearchBuildingsForContactParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.buildingsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);

                    const contactId = this.contactId();

                    if (!contactId) {
                        this.noResults.set(true);
                        params.success({
                            rowData: [],
                            rowCount: 0
                        });
                        return;
                    }

                    const searchParams = this.searchParamsBuilder(params.request);
                    const response = await this.buildingService.searchBuildingsForContact(searchParams);

                    const rowData = response?.results ?? [];
                    const totalCount = response?.pageInfo?.itemsCount ?? 0;

                    this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                    params.success({
                        rowData,
                        rowCount: totalCount
                    });
                } catch (error) {
                    console.error('Error fetching buildings:', error);
                    params.fail();
                    this.noResults.set(true);
                    this.snackbarNotificationService.error('An error occurred while trying to load the buildings');
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
